import React, { memo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Divider
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { isMacOS } from '../../util/general'

interface ChartKeyboardShortcutsHelpProps {
    open: boolean
    onClose: () => void
}

interface ShortcutGroup {
    title: string
    shortcuts: Array<{
        action: string
        keys: string[]
        keysWindows?: string[]
    }>
}

const KeyChip = ({ keyLabel }: { keyLabel: string }) => (
    <Chip
        label={keyLabel}
        size="small"
        sx={{
            fontFamily: 'monospace',
            fontWeight: 'bold',
            backgroundColor: 'action.hover',
            mx: 0.5,
            height: 24,
            fontSize: '0.75rem'
        }}
    />
)

const ChartKeyboardShortcutsHelp: React.FC<ChartKeyboardShortcutsHelpProps> = ({ open, onClose }) => {
    const isMac = isMacOS()

    const shortcutGroups: ShortcutGroup[] = [
        {
            title: 'Chart Navigation',
            shortcuts: [
                {
                    action: 'Zoom In',
                    keys: ['⌘', '↑'],
                    keysWindows: ['Ctrl', '↑']
                },
                {
                    action: 'Zoom Out',
                    keys: ['⌘', '↓'],
                    keysWindows: ['Ctrl', '↓']
                },
                {
                    action: 'Pan Left (1 bar)',
                    keys: ['←']
                },
                {
                    action: 'Pan Right (1 bar)',
                    keys: ['→']
                },
                {
                    action: 'Pan Left Fast',
                    keys: ['Shift', '←']
                },
                {
                    action: 'Pan Right Fast',
                    keys: ['Shift', '→']
                },
                {
                    action: 'Move to First Bar',
                    keys: ['Alt', '⌘', '←'],
                    keysWindows: ['Alt', 'Shift', '←']
                },
                {
                    action: 'Move to Last Bar',
                    keys: ['Alt', '⌘', '→'],
                    keysWindows: ['Alt', 'Shift', '→']
                },
                {
                    action: 'Move to Realtime',
                    keys: ['⌘', '→'],
                    keysWindows: ['Ctrl', '→']
                },
                {
                    action: 'Scroll to Recent Bar',
                    keys: ['Alt', 'Shift', '→']
                },
                {
                    action: 'Reset Chart View',
                    keys: ['Alt', 'R']
                }
            ]
        },
        {
            title: 'Alerts',
            shortcuts: [
                {
                    action: 'Create Alert (Open Dialog)',
                    keys: ['Alt', 'A']
                },
                {
                    action: 'Add Alert',
                    keys: ['A']
                },
                {
                    action: 'Open Edit Alert Dialog',
                    keys: ['Double Click']
                },
                {
                    action: 'Save Changes in Edit Dialog',
                    keys: ['⌘', 'Enter'],
                    keysWindows: ['Ctrl', 'Enter']
                },
                {
                    action: 'Next Alert/Event',
                    keys: ['↓']
                },
                {
                    action: 'Previous Alert/Event',
                    keys: ['↑']
                },
                {
                    action: 'Next Alert (Space)',
                    keys: ['Space']
                },
                {
                    action: 'Previous Alert (Space)',
                    keys: ['Shift', 'Space']
                },
                {
                    action: 'Select Alert/Event',
                    keys: ['⌘', 'Click'],
                    keysWindows: ['Ctrl', 'Click']
                },
                {
                    action: 'Select All Alerts/Events',
                    keys: ['⌘', 'A'],
                    keysWindows: ['Ctrl', 'A']
                },
                {
                    action: 'Select Next Alert/Event',
                    keys: ['Shift', '↓']
                },
                {
                    action: 'Select Previous Alert/Event',
                    keys: ['Shift', '↑']
                },
                {
                    action: 'Remove Alert (No Confirmation)',
                    keys: ['Shift', 'Backspace'],
                    keysWindows: ['Shift', 'Del']
                }
            ]
        },
        {
            title: 'Chart Display',
            shortcuts: [
                {
                    action: 'Toggle Fullscreen',
                    keys: ['Shift', 'F']
                },
                {
                    action: 'Maximize Chart',
                    keys: ['Alt', 'Enter']
                },
                {
                    action: 'Switch to Next Chart',
                    keys: ['Tab']
                },
                {
                    action: 'Switch to Previous Chart',
                    keys: ['Shift', 'Tab']
                },
                {
                    action: 'Undo',
                    keys: ['⌘', 'Z'],
                    keysWindows: ['Ctrl', 'Z']
                },
                {
                    action: 'Redo',
                    keys: ['⌘', 'Y'],
                    keysWindows: ['Ctrl', 'Y']
                },
                {
                    action: 'Open Indicator Dialog',
                    keys: ['/']
                },
                {
                    action: 'Open Chart Type Menu',
                    keys: ['Alt', 'T']
                },
                {
                    action: 'Open Chart Duration',
                    keys: ['Alt', 'G']
                },
                {
                    action: 'Custom Time Interval',
                    keys: [',']
                },
                {
                    action: 'View Keyboard Shortcuts',
                    keys: ['Shift', '?']
                }
            ]
        },
        {
            title: 'Replay Mode',
            shortcuts: [
                {
                    action: 'Open/Close Replay',
                    keys: ['Shift', 'R']
                },
                {
                    action: 'Play/Pause Replay',
                    keys: ['⌘', '↓'],
                    keysWindows: ['Ctrl', '↓']
                },
                {
                    action: 'Step Forward',
                    keys: ['⌘', '→'],
                    keysWindows: ['Ctrl', '→']
                },
                {
                    action: 'Focused Zoom',
                    keys: ['⌘', 'Mouse Wheel'],
                    keysWindows: ['Ctrl', 'Mouse Wheel']
                }
            ]
        },
        {
            title: 'Layout Management',
            shortcuts: [
                {
                    action: 'Open Multi-Pane Menu',
                    keys: ['.']
                },
                {
                    action: 'Split Vertical',
                    keys: ['⌘', 'V'],
                    keysWindows: ['Ctrl', 'V']
                },
                {
                    action: 'Split Horizontal',
                    keys: ['⌘', 'H'],
                    keysWindows: ['Ctrl', 'H']
                },
                {
                    action: 'Save Layout',
                    keys: ['⌘', 'S'],
                    keysWindows: ['Ctrl', 'S']
                },
                {
                    action: 'Delete Current Widget',
                    keys: ['⌘', 'Backspace'],
                    keysWindows: ['Ctrl', 'Del']
                }
            ]
        },
        {
            title: 'Trading Shortcuts',
            shortcuts: [
                {
                    action: 'Market Order Buy',
                    keys: ['Shift', 'B']
                },
                {
                    action: 'Market Order Sell',
                    keys: ['Shift', 'S']
                },
                {
                    action: 'Limit Order Buy',
                    keys: ['Shift', 'Alt', 'B']
                },
                {
                    action: 'Limit Order Sell',
                    keys: ['Shift', 'Alt', 'S']
                },
                {
                    action: 'Add to Watchlist',
                    keys: ['Alt', 'W']
                }
            ]
        },
        {
            title: 'Quick Widgets',
            shortcuts: [
                {
                    action: 'Show/Hide Quick Widgets',
                    keys: ['Shift', 'Q']
                },
                {
                    action: 'Open Market Depth',
                    keys: ['Shift', 'D']
                },
                {
                    action: 'Open Orders',
                    keys: ['Shift', 'O']
                },
                {
                    action: 'Open Positions',
                    keys: ['Shift', 'P']
                },
                {
                    action: 'Open Quick Trade',
                    keys: ['Shift', 'T']
                }
            ]
        },
        {
            title: 'Search & General',
            shortcuts: [
                {
                    action: 'Quick Search (Symbol)',
                    keys: ['⌘', 'K'],
                    keysWindows: ['Ctrl', 'K']
                },
                {
                    action: 'Change Symbol',
                    keys: ['Any letter key']
                },
                {
                    action: 'Security Search',
                    keys: ['Shift', 'C']
                },
                {
                    action: 'Go to Date',
                    keys: ['Alt', 'G']
                },
                {
                    action: 'Add Text Note',
                    keys: ['Alt', 'N']
                },
                {
                    action: 'Save Chart Image',
                    keys: ['Alt', '⌘', 'S'],
                    keysWindows: ['Alt', 'Ctrl', 'S']
                },
                {
                    action: 'Open Save Menu',
                    keys: ['Ctrl', 'P']
                },
                {
                    action: 'Open Multi-Pane Menu',
                    keys: ['.']
                }
            ]
        },
        {
            title: 'Indicators & Drawing Tools',
            shortcuts: [
                {
                    action: 'Trendline',
                    keys: ['Alt', 'Shift', 'T']
                },
                {
                    action: 'Horizontal Line',
                    keys: ['Alt', 'Shift', 'H']
                },
                {
                    action: 'Horizontal Ray',
                    keys: ['Alt', 'Shift', 'J']
                },
                {
                    action: 'Vertical Line',
                    keys: ['Alt', 'Shift', 'V']
                },
                {
                    action: 'Cross Line',
                    keys: ['Alt', 'Shift', 'C']
                },
                {
                    action: 'Fibonacci Retracement',
                    keys: ['Alt', 'Shift', 'F']
                },
                {
                    action: 'Rectangle',
                    keys: ['Alt', 'Shift', 'R']
                },
                {
                    action: 'Ellipse',
                    keys: ['Alt', 'Shift', 'E']
                },
                {
                    action: 'Copy Selected Drawing',
                    keys: ['⌘', 'C'],
                    keysWindows: ['Ctrl', 'C']
                },
                {
                    action: 'Paste Drawing',
                    keys: ['⌘', 'V'],
                    keysWindows: ['Ctrl', 'V']
                },
                {
                    action: 'Remove Selected Drawing',
                    keys: ['Delete']
                },
                {
                    action: 'Hide All Drawings',
                    keys: ['⌘', 'Shift', 'H'],
                    keysWindows: ['Ctrl', 'Shift', 'H']
                },
                {
                    action: 'Move Drawing Left',
                    keys: ['←']
                },
                {
                    action: 'Move Drawing Right',
                    keys: ['→']
                },
                {
                    action: 'Move Drawing Up',
                    keys: ['↑']
                },
                {
                    action: 'Move Drawing Down',
                    keys: ['↓']
                }
            ]
        }
    ]

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="div">
                    Keyboard Shortcuts
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0 }}>
                {shortcutGroups.map((group, groupIndex) => (
                    <Box key={group.title}>
                        <Box sx={{ p: 2, pb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                {group.title}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    {group.shortcuts.map((shortcut, index) => {
                                        const keysToDisplay = isMac ? shortcut.keys : (shortcut.keysWindows || shortcut.keys)
                                        return (
                                            <TableRow key={index} hover>
                                                <TableCell sx={{ pl: 3, width: '50%' }}>
                                                    <Typography variant="body2">
                                                        {shortcut.action}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ pr: 3 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                        {keysToDisplay.map((key, keyIndex) => (
                                                            <React.Fragment key={keyIndex}>
                                                                <KeyChip keyLabel={key} />
                                                                {keyIndex < keysToDisplay.length - 1 && (
                                                                    <Typography variant="body2" sx={{ mx: 0.5 }}>
                                                                        +
                                                                    </Typography>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {groupIndex < shortcutGroups.length - 1 && <Divider sx={{ my: 1 }} />}
                    </Box>
                ))}
                <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">
                        💡 <strong>Quick Tips:</strong>
                        <br />• Press <KeyChip keyLabel="⌘/Ctrl" /> + <KeyChip keyLabel="K" /> for quick symbol search
                        <br />• Press <strong>any letter key</strong> (A-Z) to change symbol instantly
                        <br />• Press <KeyChip keyLabel="Shift" /> + <KeyChip keyLabel="?" /> to view this reference anytime
                        <br />• {isMac ? 'On Mac, ⌘ = Command key' : 'On Windows, use Ctrl instead of Cmd'}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default memo(ChartKeyboardShortcutsHelp)
